/**
 * Where the API lives.
 *
 * A built SPA carries no runtime config, so the API origin has to be decided in the bundle. An
 * explicit `VITE_API_URL` wins — CI sets it, and a preview can point at staging. Otherwise it is
 * *derived from where the app is being served* rather than baked at build time:
 *
 *   - on `localhost` / loopback, the local `wrangler dev` on :8787;
 *   - anywhere else, the `api.` sibling of the current host — `app.pow-wow.ca` is served beside
 *     `api.pow-wow.ca`, so the browser already knows the answer.
 *
 * Deriving at runtime is deliberate. The old code fell back to `http://localhost:8787` whenever
 * `VITE_API_URL` was absent, so a production build made without that env var — a manual
 * `deploy:production`, say — shipped a site that called each visitor's own machine and failed every
 * request with a misleading 401. The host is the one thing a deployed bundle can always trust.
 */
export const API_BASE_URL = resolveApiBaseUrl();

function resolveApiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_URL;
  if (configured) return configured;

  if (typeof window !== "undefined") {
    const { hostname, protocol } = window.location;
    if (isLoopback(hostname)) return "http://localhost:8787";
    // `app.pow-wow.ca` → `api.pow-wow.ca`, and `app-uat.pow-wow.ca` → `api-uat.pow-wow.ca`: the
    // deployment suffix travels with the label, because the app and the API it belongs to are
    // always siblings. A host with no `app.`/`www.` label gets `api.` prepended.
    const apiHost = hostname.replace(/^(?:app|www)(-[a-z0-9]+)?\./, "api$1.");
    const host = apiHost === hostname ? `api.${hostname}` : apiHost;
    return `${protocol}//${host}`;
  }

  // No window (SSR, tests) and no override — the local dev default is the only safe guess.
  return "http://localhost:8787";
}

function isLoopback(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
}
